import { Link } from "@tanstack/react-router";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronUp, Download } from "lucide-react";
import { CategoryTag } from "@/components/skills/CategoryTag";
import { RiskBadge } from "@/components/skills/RiskBadge";
import { Stars } from "@/components/skills/Stars";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { daysSince } from "@/lib/skills/catalog";
import { RISK } from "@/lib/skills/constants";
import { initials } from "@/lib/skills/format";
import type { SortKey } from "@/lib/skills/search-params";
import { asSortKey, compareByKey, type SortDir } from "@/lib/skills/sort";
import { timeAgo } from "@/lib/skills/time-ago";
import type { SkillListItem } from "@/lib/skills/types";
import { cn } from "@/lib/utils";

const NUMERIC = new Set<string>(["rating", "downloads", "updated"]);

const columns: ColumnDef<SkillListItem>[] = [
  {
    id: "title",
    accessorKey: "title",
    sortingFn: (a, b) => compareByKey(a.original, b.original, "title"),
    sortDescFirst: false,
    cell: ({ row }) => (
      <div className="max-w-[340px]">
        <Link
          to="/skill/$id"
          params={{ id: row.original.slug }}
          className="block truncate font-semibold text-navy hover:text-fg-link"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.title}
        </Link>
        <div className="truncate text-xs font-normal text-fg-3">{row.original.desc}</div>
      </div>
    ),
  },
  {
    id: "cat",
    accessorFn: (s) => s.cat[0] ?? "",
    sortingFn: (a, b) => compareByKey(a.original, b.original, "cat"),
    sortDescFirst: false,
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.cat.map((c) => (
          <CategoryTag key={c} category={c} withIcon={false} />
        ))}
      </div>
    ),
  },
  {
    id: "risk",
    accessorFn: (s) => RISK[s.risk].rank,
    sortDescFirst: false,
    cell: ({ row }) => <RiskBadge level={row.original.risk} />,
  },
  {
    id: "rating",
    accessorKey: "rating",
    sortDescFirst: true,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Stars value={row.original.rating} />
      </div>
    ),
  },
  {
    id: "downloads",
    accessorKey: "downloads",
    sortDescFirst: true,
    cell: ({ row }) => (
      <span className="inline-flex items-center justify-end gap-1.5 text-sm text-fg-2 tabular-nums">
        <Download className="size-3.5 text-fg-3" />
        {row.original.downloads.toLocaleString()}
      </span>
    ),
  },
  {
    id: "updated",
    accessorFn: (s) => daysSince(s.updatedAt),
    sortDescFirst: false,
    cell: ({ row }) => (
      <span className="text-sm text-fg-3 tabular-nums">
        {timeAgo(new Date(row.original.updatedAt))}
      </span>
    ),
  },
  {
    id: "author",
    accessorFn: (s) => s.authorName,
    sortingFn: (a, b) => compareByKey(a.original, b.original, "author"),
    sortDescFirst: false,
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-2 text-sm text-fg-2">
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-[image:var(--nymbl-gradient)] text-[10px] font-bold text-white">
          {initials(row.original.authorName)}
        </span>
        {row.original.authorName}
      </span>
    ),
  },
  {
    id: "version",
    accessorKey: "version",
    enableSorting: false,
    cell: ({ row }) => <span className="font-mono text-xs text-fg-3">v{row.original.version}</span>,
  },
];

const HEADER_LABELS: Record<string, string> = {
  title: "Skill",
  cat: "Category",
  risk: "Risk",
  rating: "Rating",
  downloads: "Installs",
  updated: "Updated",
  author: "Author",
  version: "Version",
};

export function SkillTable({
  rows,
  sort,
  dir,
  onSortChange,
  onOpen,
}: {
  /** Filtered rows, pre-ordered by relevance (the resting state). */
  rows: SkillListItem[];
  sort: SortKey;
  dir: SortDir;
  onSortChange: (sort: SortKey, dir: SortDir) => void;
  onOpen: (id: string) => void;
}) {
  // relevance is the resting order (no active column) — represent it as empty sorting.
  const sorting: SortingState = sort === "relevance" ? [] : [{ id: sort, desc: dir === "desc" }];

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      const key = first ? asSortKey(first.id) : null;
      if (!first || !key) {
        onSortChange("relevance", "desc");
      } else {
        onSortChange(key, first.desc ? "desc" : "asc");
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    enableMultiSort: false,
  });

  return (
    <div className="overflow-hidden rounded-[14px] border border-border-1 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {table.getHeaderGroups()[0]?.headers.map((header) => {
              const col = header.column;
              const numeric = NUMERIC.has(col.id);
              const sortedDir = col.getIsSorted(); // 'asc' | 'desc' | false
              const label = HEADER_LABELS[col.id] ?? col.id;
              return (
                <TableHead
                  key={header.id}
                  aria-sort={
                    sortedDir ? (sortedDir === "asc" ? "ascending" : "descending") : undefined
                  }
                  className={cn(numeric && "text-right")}
                >
                  {col.getCanSort() ? (
                    <button
                      type="button"
                      onClick={col.getToggleSortingHandler()}
                      className={cn(
                        "group inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-fg-link",
                        sortedDir ? "text-navy" : "text-fg-3",
                      )}
                    >
                      {label}
                      <ChevronUp
                        aria-hidden
                        className={cn(
                          "size-3.5 transition",
                          sortedDir
                            ? "text-fg-link opacity-100"
                            : "opacity-0 group-hover:opacity-45",
                          sortedDir === "desc" && "rotate-180",
                        )}
                      />
                    </button>
                  ) : (
                    label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              onClick={() => onOpen(row.original.slug)}
              className="cursor-pointer hover:bg-bg-2"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(NUMERIC.has(cell.column.id) && "text-right")}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
